import type { CCPreset } from '~/types/presets';
import type { MidiCC, DeviceProfile } from '~/types/midi';

/**
 * "Whaat?" CC preset for YO! FM Synth
 *
 * This preset features low harmonicity (CC15: 8) and low modulation index (CC14: 11),
 * creating smooth, bell-like tones with subtle FM character. The high filter cutoff
 * (CC70: 105) keeps the sound bright and open, while moderate resonance (CC71: 36)
 * adds a touch of character without being overwhelming.
 *
 * Key characteristics:
 * - Low harmonicity (CC15: 8) for smooth, consonant tones
 * - Low modulation index (CC14: 11) for subtle FM effects
 * - High filter cutoff (CC70: 105) for bright, open sound
 * - Moderate amp release (CC72: 47) and sustain (CC73: 70)
 * - Clean, bell-like character with gentle FM modulation
 */

const whaatFMSynthCCs: MidiCC[] = [
  { number: 1, value: 0, scramble: false, smooth: false, show: true, min: 0, max: 127 },
  { number: 7, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 11, value: 100, scramble: false, smooth: false, show: true, min: 80, max: 110 },
  { number: 14, value: 11, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 15, value: 8, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 70, value: 105, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 71, value: 36, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 72, value: 47, scramble: true, smooth: false, show: true, min: 0, max: 127 },
  { number: 73, value: 70, scramble: true, smooth: false, show: true, min: 0, max: 127 },
];

/**
 * Device profile for "Whaat?" FM Synth preset
 */
const whaatFMSynthProfile: DeviceProfile = {
  id: 'yo-fm-synth-whaat',
  name: 'Whaat?',
  deviceName: 'YO! FM Synth',
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    {
      number: 1,
      enabled: false,
      min: 0,
      max: 127,
      label: 'Modulation',
    },
    {
      number: 7,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Volume',
    },
    {
      number: 11,
      enabled: false,
      min: 80,
      max: 110,
      label: 'Expression',
    },
    {
      number: 14,
      enabled: true, // ✅ Modulation Index
      min: 0,
      max: 127,
      label: 'Modulation Index',
    },
    {
      number: 15,
      enabled: true, // ✅ Harmonicity
      min: 0,
      max: 127,
      label: 'Harmonicity',
    },
    {
      number: 70,
      enabled: true, // ✅ Filter Cutoff
      min: 0,
      max: 127,
      label: 'Filter Cutoff',
    },
    {
      number: 71,
      enabled: true, // ✅ Filter Resonance
      min: 0,
      max: 127,
      label: 'Filter Resonance',
    },
    {
      number: 72,
      enabled: false, // Disabled - too short causes abrupt cutoff, keep preset value
      min: 0,
      max: 127,
      label: 'Carrier Release',
    },
    {
      number: 73,
      enabled: false, // Disabled - too short causes clicks, keep preset value
      min: 0,
      max: 127,
      label: 'Carrier Attack',
    },
  ],
};

/**
 * "Whaat?" FM Synth CC Preset
 */
export const WHAAT_FM_SYNTH_PRESET: CCPreset = {
  id: 'whaat-fm',
  name: 'Whaat?',
  deviceType: 'FMSynth',
  cc: whaatFMSynthCCs,
  profiles: [whaatFMSynthProfile],
  activeProfileId: 'yo-fm-synth-whaat',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  userId: null, // System preset
  isPublic: true,
};

/**
 * Get the "Whaat?" FM Synth CC preset
 * Returns a deep copy to avoid mutation
 */
export function getWhaatFMSynthPreset(): CCPreset {
  return {
    ...WHAAT_FM_SYNTH_PRESET,
    cc: WHAAT_FM_SYNTH_PRESET.cc.map(cc => ({ ...cc })),
    profiles: WHAAT_FM_SYNTH_PRESET.profiles.map(profile => ({
      ...profile,
      ccConfig: profile.ccConfig.map(config => ({ ...config })),
    })),
  };
}
